<template>
    <e-iframe-page :loading="loading" ref="iframePage" :title="'Website Settings|Paramètres du site' | tr"
        body-class="bg-white text-left" body-width="900">

        <div class="text-center">
            <b-img center width="200" :src="cmsWebsite.design.logo.src" />
            <h3 class="title mt-2" v-tr>
                {{ cmsWebsite.name }} Settings|Paramètres de {{ cmsWebsite.name | tr }}
            </h3>
        </div>

        <b-tabs class="tabs-pills mt-3" justified>
            <b-tab :title="'General|Général' | tr" class="py-3 px-1">
                <b-form-group :label="'Site Name|Nom du site' | tr">
                    <e-input type="name" v-model="cmsWebsite.name" />
                    <div class="form-help mt-2" v-tr>
                        Name is for internal use only|Le nom du site est pour usage interne seulement
                    </div>
                </b-form-group>

                <hr>

                <b-form-group :label="'Site Title|Titre du site' | tr">
                    <e-translation-input v-model="cmsWebsite.meta.title" :use-locales="cmsLocales" auto-translate />
                    <div class="form-help mt-2" v-tr>
                        Your site title appears in your browser tab, along with your favicon|
                        Le titre de votre site apparaît dans l'onglet du navigateur, à côté du favicon
                    </div>
                </b-form-group>

                <hr>


                <b-form-group :label="'Favicon' | tr">
                    <input-image v-model="cmsWebsite.design.favicon.src" category="favicon" />
                    <div class="form-help mt-2" v-tr>
                        Square image that appears in the browser tab next to the title<br>Recommended size: 32x32px|
                        Image carrée qui apparaît dans l'onglet du navigateur à côté du titre<br>Taille recommandée :
                        32x32px
                    </div>
                </b-form-group>
                <b-form-group :label="'Logo' | tr">
                    <input-image v-model="cmsWebsite.design.logo.src" size="lg" category="logo" />
                    <div class="form-help mt-2" v-tr>
                        Recommended size: 250x150px|Taille recommandée : 250x150px
                    </div>
                </b-form-group>

                <hr>

                <b-form-group :label="'Domain Name|Nom de domaine' | tr">
                    <b-input-group>
                        <b-input-group-text>https://</b-input-group-text>
                        <e-input type="text" v-model="cmsWebsite.domain" :readonly="!canAdminEyeInWireless()" :state="validateDomain(cmsWebsite.domain)"/>
                    </b-input-group>
                    <e-form-tooltip v-if="!validateDomain(cmsWebsite.domain)" variant="danger">
                        <span v-tr>Invalid character (only letters, numbers, "." and "-" are allowed)|Charactères non autorisés (seuls les lettres, chiffres "." et "-" sont autorisés)</span>
                    </e-form-tooltip>
                    <div class="form-help mt-2" v-tr>
                        All your page URLs starts with the domain name|
                        Toutes les URLs de vos pages commencent par le nom de domaine
                    </div>
                </b-form-group>

                <b-form-group :label="'Aliases (Alt Domains)|Alias (domaines alternatifs)' | tr">
                    <b-list-group>
                        <b-list-group-item v-for="(alias, index) in cmsWebsite.aliases"
                            class="d-flex align-items-center interactive p-1" :key="`script-${index}`"
                            :title="alias.comment">
                            <b-input-group>
                                <b-select v-model="alias.env" style="max-width: 90px;">
                                    <option value="LIVE">PROD</option>
                                    <option value="DEMO">DEMO</option>
                                    <option value="DEV">DEV</option>
                                    <option value="LOCAL">LOCAL</option>
                                </b-select>
                                <b-input-group-text>https://</b-input-group-text>
                                <e-input type="text" v-model="alias.domain" :readonly="!canAdminEyeInWireless()" :state="validateDomain(alias.domain)"/>
                            </b-input-group>

                            <e-form-tooltip v-if="!validateDomain(alias.domain)" variant="danger" no-triangle>
                                <span v-tr>Invalid character (only letters, numbers, "." and "-" are allowed)|Charactères non autorisés (seuls les lettres, chiffres "." et "-" sont autorisés)</span>
                            </e-form-tooltip>
                            <e-button-confirm v-if="canAdminEyeInWireless()" @click="removeAlias(index)" variant="light"
                                class="ml-2">
                                <i class="fas fa-trash" />
                            </e-button-confirm>
                        </b-list-group-item>
                    </b-list-group>
                </b-form-group>

                <div class="text-center mt-3">
                    <b-btn variant="light-primary" @click="newAlias">
                        <span v-tr>Add Alias|Ajouter alias</span>
                        <i class="fas fa-plus ml-1" />
                    </b-btn>
                </div>
            </b-tab>
            <b-tab :title="'Languages|Langues' | tr" class="py-3 px-1">
                <h4 class="mb-4" v-tr>Languages|Langues</h4>

                <b-form-group :label="'Languages|Langues' | tr">
                    <e-treeselect v-model="selectedLanguages" :options="supportedLocalesList" track-by="locale"
                        label="name" multiple :flat="true" :close-on-select="false" :clearable="false"
                        prepend-icon="fas fa-language" :placeholder="'Select languages|Selectionnez les langues' | tr"
                        variant="simple">
                        <template #option-label="{ node }">
                            <span class="d-flex align-items-center">
                                <img :src="node.flag" :alt="node.locale" class="flag-icon mr-2">
                                <span>{{ node.name }}</span>
                                <span class="ml-1">({{ node.locale }})</span>
                            </span>
                        </template>
                        <template #selected-pill-label="{ node }">
                            <span class="d-flex align-items-center">
                                <img :src="node.flag" :alt="node.locale" class="flag-icon mr-1">
                                <span>{{ node.name }}</span>
                            </span>
                        </template>
                    </e-treeselect>
                    <div class="form-help mt-2" v-tr>
                        The languages selected here will be available when editing content|
                        Les langues selectionnées ici seront disponibles lors de l'édition du contenu
                    </div>
                </b-form-group>

                <hr>

                <b-form-group :label="'Default language|Langue par défaut' | tr">
                    <b-select v-model="fallbackLanguage" :options="languageOptions"></b-select>
                    <div class="form-help mt-2" v-tr>
                        Used when a translation is missing|Utilisée uniquement quand une traduction est manquante
                    </div>
                </b-form-group>

                <b-form-group :label="tr(`Input display language|Langue pour l'éditeur`)">
                    <b-select v-model="inputDisplayLanguage" :options="languageOptions"></b-select>
                    <div class="form-help mt-2" v-tr>
                        Choose the language shown first in translation inputs|
                        Choisissez la langue à afficher dans les champs de traduction
                    </div>
                </b-form-group>

                <b-form-group :label="'Language selector|Sélecteur de langue' | tr">
                    <b-checkbox switch v-model="cmsWebsite.settings.languageModalShowFlags">
                        <span v-tr>Show language flags in selector|Afficher les drapeaux dans le sélecteur</span>
                    </b-checkbox>
                    <div class="form-help mt-2" v-tr>
                        Show or hide flags in the language selection modal|
                        Afficher ou masquer les drapeaux dans la fenêtre de sélection de langue
                    </div>
                    <b-btn variant="light-primary" class="mt-2" @click="previewLanguageSelector">
                        <i class="fas fa-eye mr-1" />
                        <span v-tr>Preview language modal|Prévisualiser la fenêtre de langue</span>
                    </b-btn>
                </b-form-group>

                <hr>

                <b-card class="simple">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                        <h5 class="mb-0" v-tr>Remove language content|Supprimer le contenu de la langue</h5>
                        <b-badge variant="light" v-tr>Coming soon|Bientot</b-badge>
                    </div>
                    <div class="form-help" v-tr>
                        This will remove all texts for the selected language|
                        Cette option supprimera tous les textes pour la langue selectionnée
                    </div>
<!--                    <div class="d-flex align-items-center mt-2">-->
<!--                        <b-select v-model="languageToClear" :options="languageOptions" disabled></b-select>-->
<!--                        <b-btn class="ml-2" variant="light" disabled>Coming soon</b-btn>-->
<!--                    </div>-->
                </b-card>
            </b-tab>
            <b-tab :title="'Integrations|Intégrations' | tr" v-if="canAdminEyeInWireless()" class="py-3 px-1">
                <h4 class="mb-4" v-tr>Integrations|Intégrations</h4>

                <b-form-group>
                    <b-checkbox switch v-model="cmsWebsite.integrations.map.enabled">
                        <span v-tr>Map Integration</span>
                    </b-checkbox>
                </b-form-group>

                <b-form-group :label="'Select Map|Selectionner le plan' | tr" v-if="cmsWebsite.integrations.map.enabled">
                    <b-select v-model="cmsWebsite.integrations.map.mapId">
                        <option v-for="map in availableMaps" :key="map._id" :value="map._id">
                            {{ map.name }}
                        </option>
                    </b-select>
                </b-form-group>
            </b-tab>

            <b-tab title="Pages" v-if="canAdminEyeInWireless()" class="py-3 px-1">
                <div>
                    <h4>Pages</h4>
                    <div class="form-help mb-2" v-tr>
                        Select which pages should be included on the website.|
                        Choisissez quelles pages doivent être inclues dans le site internet.
                    </div>
                </div>
                <b-row>
                    <b-col cols="8">
                        <e-search-input v-model="websitePages.searchPagesQuery"
                            :placeholder="tr(`Search page|Chercher une page`)" />
                    </b-col>
                    <b-col cols="4">
                        <b-input-group>
                            <b-input-group-text>
                                <i class="fas fa-folder-tree" />
                            </b-input-group-text>
                            <b-select v-model="websitePages.filter" :options="websitePages.options">
                                <option value="all" v-tr>All Pages|Toutes les pages</option>
                                <option value="" v-tr>No Category|Sans catégorie</option>
                                <option v-for="category in categories" :key="category.key" :value="category.key">
                                    {{ category.name | tr }}
                                </option>
                            </b-select>
                        </b-input-group>
                    </b-col>
                </b-row>
                <b-list-group class="mt-3">
                    <b-list-group-item v-for="page in filteredWebsitePages" :key="page.id"
                        class="d-flex align-items-center px-3">
                        <b-checkbox switch size="lg" :checked="isInCmsPages(page)"
                            @change="onCmsPageToggle(page, $event)"
                            :title="tr(`Enable/Disable page|Activer/Désactiver la page`)" />

                        <div class="d-flex flex-column mr-auto">
                            <h4 class="my-0">
                                <i v-if="page.glyph" class="fa-fw" :class="[fontAwesomePrefix, page.glyph]" /> {{
                                        page.name | tr
                                }}
                            </h4>
                            <p class="text-muted mt-2 mb-0" style="font-size: 0.9em;">
                                <template v-if="page.category">
                                    {{ categoryName(page.category) | tr }}
                                </template>
                                <i v-else v-tr>No category|Pas de catégorie</i>
                                <i class="fas fa-caret-right" />
                                <template v-if="tr(page.meta.title)">{{ page.meta.title | tr }}</template>
                                <i v-else class="text-warning" v-tr>Missing Page Title|Titre de page manquant</i>
                            </p>
                        </div>

                        <b-dropdown variant="light" @click="openPage(page)" size="sm" split class="ml-2" right>
                            <template #button-content>
                                <i class="fas fa-external-link-alt mr-1" />
                                <span v-tr>Open|Ouvrir</span>
                            </template>
                            <b-dropdown-item @click="editPage(page)">
                                <i class="fas fa-pen" />
                                <span v-tr>Edit|Modifier</span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-list-group-item>
                </b-list-group>
            </b-tab>
            <b-tab :title="'SEO' | tr" class="py-3 px-1">
                <h4 class="mb-4" v-tr>Search Engine Optimization (SEO)|Optimisation des moteurs de recherche (SEO)</h4>

                <e-translation-input v-model="cmsWebsite.meta.title" @change="syncSocial()"
                    label="SEO page Title|Titre de la page pour SEO" :use-locales="cmsLocales" />
                <hr>
                <e-translation-input v-model="cmsWebsite.meta.description" @change="syncSocial()" type="textarea"
                    label="SEO page Description|Description de la page pour SEO" :use-locales="cmsLocales" />
                <b-form-group :label="'Search Engine Visibility|Visibility des moteurs de recherche' | tr">
                    <b-select v-model="cmsWebsite.meta.searchVisibility">
                        <option value="visible" v-tr>Visible to search engines|Visible par les moteurs de recherche
                        </option>
                        <option value="hidden" v-tr>Hidden from search engines results|Caché des résultats des moteurs
                            de recherche
                        </option>
                    </b-select>
                </b-form-group>
            </b-tab>
            <b-tab :title="'Soc. Med.|Rés. soc.' | tr" class="py-3 px-1">
                <h4 class="mb-4" v-tr>Social Media|Réseaux sociaux</h4>

                <b-form-group :label="'Social Image|Image aperçu' | tr">
                    <input-image v-model="cmsWebsite.meta.socialImage" size="xl" />
                </b-form-group>

                <e-translation-input v-model="cmsWebsite.meta.socialTitle"
                    label="Social page Title|Titre pour réseaux sociaux"
                    :readonly="cmsWebsite.meta.socialTitle.sameAsSEO" :use-locales="cmsLocales" />
                <b-checkbox v-model="cmsWebsite.meta.socialTitle.sameAsSEO"><span v-tr>Same as SEO page title|Identique
                        au titre de la page SEO</span>
                </b-checkbox>
                <hr>
                <e-translation-input v-model="cmsWebsite.meta.socialDescription" type="textarea"
                    label="Social page Description|Description page pour réseaux sociaux"
                    :readonly="cmsWebsite.meta.socialDescription.sameAsSEO" :use-locales="cmsLocales" />
                <b-checkbox v-model="cmsWebsite.meta.socialDescription.sameAsSEO"><span v-tr>Same as SEO page
                        title|Identique au titre de la page SEO</span>
                </b-checkbox>
            </b-tab>
            <b-tab :title="'Analytics|Analytique' | tr" class="py-3 px-1">
                <h4 class="mb-4" v-tr>Tracking Tools|Outils d'analytique</h4>

                <b-form-group>
                    <template #label>
                        <i class="fab fa-google mr-1" /> Google Analytics / Tags Manager
                    </template>
                    <e-input v-model="cmsWebsite.settings.analytics.googleTags" placeholder="G-1234567890"
                        maxlength="20" style="max-width: 200px" />
                    <div class="form-help mt-2" v-tr>
                        Sign up for Google Analytics and follow the instructions to get your Tracking ID.|
                        Inscrivez-vous à Google Analytics et suivez les instructions pour obtenir votre identifiant de
                        suivi.
                    </div>
                </b-form-group>

                <hr>

                <b-form-group>
                    <template #label>
                        <i class="fab fa-facebook mr-1" /> Meta Pixel (Facebook Pixel)
                    </template>
                    <e-input v-model="cmsWebsite.settings.analytics.metaPixel" placeholder="123456789" maxlength="20"
                        style="max-width: 200px" />
                    <div class="form-help mt-2" v-tr>
                        Sign up for Meta Business and follow the instructions to get your Pixel ID.|
                        Inscrivez-vous à Meta Business et suivez les instructions pour obtenir votre identifiant Meta
                        Pixel.
                    </div>
                </b-form-group>

                <hr>

                <h4 v-tr>Custom Codes & Scripts|Code personnalisé et scripts</h4>

                <b-list-group>
                    <b-list-group-item v-for="(script, index) in cmsWebsite.settings.scripts"
                        class="d-flex align-items-center interactive" :key="`script-${index}`"
                        @click="editScript(script)">
                        <span class="name">{{ script.name }}</span>
                        <!--                        <b-btn @click="editScript(script)" variant="light" class="ml-auto" v-tr>Edit|Modifier</b-btn>-->
                        <b-btn @click="removeScript(index)" variant="danger" class="ml-2"><i class="fas fa-trash" />
                        </b-btn>
                    </b-list-group-item>
                </b-list-group>

                <div class="text-center mt-3">
                    <b-btn variant="light-primary" @click="newScript">
                        <span v-tr>Add New Code|Ajouter nouveau code</span>
                        <i class="fas fa-plus ml-1" />
                    </b-btn>
                </div>
            </b-tab>
            <b-tab :title="'Redirects|Redirections' | tr" class="py-3 px-1">
                <h4 class="mb-4" v-tr>Redirectors|Redirections</h4>

                <b-list-group>
                    <b-list-group-item v-for="(redirect, index) in cmsWebsite.routing.redirects"
                        class="d-flex align-items-center interactive py-1 pl-2 pr-1" @click="editRedirect(redirect)"
                        :key="`script-${index}`" :title="redirect.comment">
                        <b-row>
                            <b-col><i class="fas fa-link mr-1" /> {{ redirect.from }}</b-col>
                            <b-col><i class="fas fa-arrow-right mr-1" /> {{ redirect.to }}</b-col>
                        </b-row>
                        <!--                        <b-btn @click="editRedirect(redirect)" variant="light" class="ml-auto" v-tr>Edit|Modifier</b-btn>-->
                        <b-btn @click="removeRedirect(index)" variant="danger" class="ml-2"><i class="fas fa-trash" />
                        </b-btn>
                    </b-list-group-item>
                </b-list-group>

                <div class="text-center mt-3">
                    <b-btn variant="light-primary" @click="newRedirect">
                        <span v-tr>Add Redirect|Ajouter redirection</span>
                        <i class="fas fa-plus ml-1" />
                    </b-btn>
                </div>
            </b-tab>
            <b-tab :title="'Advanced|Avancés' | tr" v-if="canAdminEyeInWireless()" class="py-3 px-1">
                <h4 class="mb-4" v-tr>Integrations|Intégrations</h4>

                <b-form-group :label="'Password|Mot de passe' | tr">
                    <b-input-group>
                        <b-input-group-text><i class="fas fa-key"/></b-input-group-text>
                        <e-input type="password" v-model="cmsWebsite.settings.password" />
                    </b-input-group>
                    <div class="form-help mt-2" v-tr>
                        To prevent users from accessing the website until it's ready.|
                        Pour empêcher les utilisateurs d'accéder au site avant qu'il ne soit prêt.
                    </div>
                </b-form-group>

                <hr>

                <h4 v-tr>Tests et Feedback|Tests & Feedback</h4>

                <b-form-group class="mt-3">
                    <b-checkbox v-model="cmsWebsite.settings.testing.enabled">
                        <span v-tr>Enable feedback tools|Activer l'outil de commentaires</span>
                    </b-checkbox>
                </b-form-group>

                <b-form-group :label="'Message for testers|Message pour les testeurs' | tr">
                    <e-translation-input v-model="cmsWebsite.settings.testing.testersMessage" type="textarea"/>
                    <div class="form-help mt-2" v-tr>
                        Used in the feedback window|
                        Utilisé dans la fenêtre de commentaires
                    </div>
                </b-form-group>
            </b-tab>
        </b-tabs>

        <template #footer>
            <b-btn variant="primary" @click="save()">
                <span v-tr>Save|Enregistrer</span>
            </b-btn>
        </template>

        <b-modal v-if="scriptSelected" size="lg" v-model="scriptModalVisible"
            :title="'Add/Edit Code|Ajouter/Modifier du code' | tr">
            <b-form-group :label="'Custom Code Name|Nom du code personnalisé' | tr">
                <e-input type="name" v-model="scriptSelected.name"
                    :placeholder="'Name your custom code|Donnez un nom à votre code personnalisé' | tr" />
            </b-form-group>
            <b-form-group :label="'Custom Code|Code personnalisé' | tr">
                <b-textarea v-model="scriptSelected.code" style="min-height: 200px;"
                    :placeholder="'Add you custom <meta>, <script>, or <noscript> code|Ajoutez votre code <meta>, <script>, ou <noscript>' | tr" />
            </b-form-group>
            <b-form-group :label="'Custom Code|Code personnalisé' | tr">
                <b-select v-model="scriptSelected.appendTo">
                    <option class="f-700" value="head">&lt;head&gt; {{ tr('(Recommended)|(Recommandé)') }}</option>
                    <option value="body">{{ tr('End of|À la fin du') }} &lt;body&gt;</option>
                </b-select>
            </b-form-group>
            <div class="form-help mt-2" v-tr>
                This code will be added to every page.|
                Ce code sera ajouté sur toutes les pages
            </div>

            <template #modal-footer="{ hide }">
                <b-btn variant="light" @click="hide"><span v-tr>Cancel|Annuler</span></b-btn>
                <b-btn variant="primary" v-if="scriptMode === 'add'" @click="addScript"><span v-tr>Add|Ajouter</span>
                </b-btn>
                <b-btn variant="primary" v-else @click="saveScript"><span v-tr>Apply|Appliquer</span></b-btn>
            </template>
        </b-modal>

        <b-modal v-if="redirectSelected" size="lg" v-model="redirectModalVisible"
            :title="'Add/Edit Redirect|Ajouter/Modifier une redirection' | tr">
            <b-form-group :label="'From|De' | tr">
                <e-input v-model="redirectSelected.from" :placeholder="tr('Ex: /old-page-url|Ex: /ancienne-url')" />
            </b-form-group>
            <div class="form-help" v-tr>Must start with /|Doit commencer par /</div>
            <b-form-group>
                <b-checkbox v-model="redirectSelected.exactMatch">
                    <span v-tr>Redirect only if matches exactly|Rediriger seulement en cas de correspondance
                        exacte</span>
                </b-checkbox>
            </b-form-group>
            <b-form-group :label="'Redirect to|Rediriger vers' | tr">
                <e-input v-model="redirectSelected.to" :placeholder="tr('Ex: /redirect-to-page|Ex: /url-a-rediriger')"
                    maxlength="1000" />
            </b-form-group>
            <b-form-group :label="'Comment|Commentaire' | tr">
                <b-textarea v-model="redirectSelected.comment" style="min-height: 120px;"
                    :placeholder="'Add a comment|Ajouter un commentaire' | tr" />
            </b-form-group>

            <template #modal-footer="{ hide }">
                <b-btn variant="light" @click="hide"><span v-tr>Cancel|Annuler</span></b-btn>
                <b-btn variant="primary" v-if="redirectMode === 'add'" @click="addRedirect"><span
                        v-tr>Add|Ajouter</span></b-btn>
                <b-btn variant="primary" v-else @click="saveRedirect"><span v-tr>Apply|Appliquer</span></b-btn>
            </template>
        </b-modal>
    </e-iframe-page>
</template>

<script>
import InputImage from "@/components/editor/properties/inputs/input-image.vue";
import TabHeader from "@/layout/tab-header.vue";
import { getSupportedLocales, getLocale, setLocale, getFallbackLocale, setFallbackLocale, normalizeLocaleCode } from "@vue-components/helpers/I18nPluginHelper.js";
import EButtonConfirm from "../../vue-components/components/e-button-confirm.vue";
import EEmptyPlaceholder from "../../vue-components/components/e-empty-placeholder.vue";
import EInput from "../../vue-components/components/e-input.vue";
import NetworkV2 from "../../vue-components/helpers/NetworkV2.js";
import ETranslationInput from "../../vue-components/components/e-translation-input.vue";
import EIframePage from "../../vue-components/components/e-iframe-page.vue";
import ESearchInput from "../../vue-components/components/e-search-input.vue";
import ETreeselect from "../../vue-components/components/e-treeselect.vue";
import EFormTooltip from "@vue-components/components/e-form-tooltip.vue";

// iframe-website-settings #hot-reload-debug
export default {
    name: `iframe-website-settings`,
    components: {EFormTooltip, ETreeselect, ESearchInput, EButtonConfirm, InputImage, EInput, EEmptyPlaceholder, ETranslationInput, TabHeader, EIframePage },
    data() {
        return {
            loading: false,
            scriptSelected: null,
            categories: [],
            websitePages: {
                pages: [],
                searchPagesQuery: ``,
                selected: `all`,
                filter: `all`,
                options: []
            },
            scriptMode: `add`,
            scriptModalVisible: false,
            redirectSelected: null,
            redirectMode: `add`,
            redirectModalVisible: false,
            availableMaps: [],
            languageToClear: null
        }
    },
    computed: {
        supportedLocalesList() {
            return getSupportedLocales();
        },
        selectedLanguages: {
            get() {
                const cmsWebsiteLanguages = this.normalizeLanguageCodes(this.cmsWebsite?.settings?.languages);
                if (cmsWebsiteLanguages.length) {
                    return getSupportedLocales().filter(lang => cmsWebsiteLanguages.includes(lang.locale));
                }
                const fallback = getFallbackLocale();
                return getSupportedLocales().filter(lang => lang.locale === fallback);
            },
            set(langs) {
                const locales = this.normalizeLanguageCodes(langs);
                const fallback = getFallbackLocale();
                const nextLocales = locales.length ? locales : [fallback];
                this.$set(this.cmsWebsite.settings, `languages`, nextLocales);
                this.ensureFallbackLanguage(nextLocales);
                this.ensurePreviewLanguage(nextLocales);
                if (!this.languageToClear || !nextLocales.includes(this.languageToClear)) {
                    this.languageToClear = nextLocales[0] || fallback;
                }
            }
        },
        languageOptions() {
            return this.selectedLanguages.map(lang => ({
                value: lang.locale,
                text: `${lang.name} (${String(lang.locale).toUpperCase()})`
            }));
        },
        inputDisplayLanguage: {
            get() {
                const current = normalizeLocaleCode(getLocale());
                const fallback = this.fallbackLanguage;
                if (current && this.selectedLanguages.some(lang => lang.locale === current)) {
                    return current;
                }
                return fallback;
            },
            set(value) {
                const normalized = normalizeLocaleCode(value) || this.fallbackLanguage;
                this.setPreviewLanguage(normalized);
            }
        },
        fallbackLanguage: {
            get() {
                const stored = normalizeLocaleCode(this.cmsWebsite?.settings?.fallbackLanguage);
                return stored || getFallbackLocale();
            },
            set(value) {
                const normalized = normalizeLocaleCode(value) || getFallbackLocale();
                this.$set(this.cmsWebsite.settings, `fallbackLanguage`, normalized);
                setFallbackLocale(normalized);
                this.ensurePreviewLanguage(this.normalizeLanguageCodes(this.cmsWebsite?.settings?.languages));
            }
        },
        dealerIds() {
            return this.$store.state.dealers.map(dealer => {
                return dealer.dealerid;
            });
        },
        isInCmsPages() {
            return (page) => {
                const pages = this.cmsWebsite?.pages || [];
                return pages.some(p => p.id === page._id);
            };
        },
        filteredWebsitePages() {
            const q = (this.websitePages.searchPagesQuery || ``).trim().toLowerCase();
            const filter = this.websitePages.filter;

            return (this.websitePages.pages || [])
                .filter(page => {
                    if (filter === `all`) return true;
                    if (!filter && !page.category) return true;
                    return page.category === filter;
                })
                .filter(page => {
                    if (page.id && String(page.id).toLowerCase().includes(q)) return true;
                    if (page.slug && String(page.slug).toLowerCase().includes(q)) return true;
                    if (page.name && typeof page.name === `object`) {
                        for (const k in page.name) {
                            const v = page.name[k];
                            if (v && String(v).toLowerCase().includes(q)) return true;
                        }
                    } else if (typeof page.name === `string` && page.name.toLowerCase().includes(q)) {
                        return true;
                    }
                    return false;
                }).sort((a, b) => {
                    const nameA = this.tr(a.name);
                    const nameB = this.tr(b.name);
                    return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
                });
        }
    },
    mounted() {
        if (!this.cmsWebsite.meta) {
            this.cmsWebsite.meta = {
                title: {},
                description: {},
                keywords: {},
                searchVisibility: `visible`,
                socialImage: null,
                socialTitle: {
                    sameAsSEO: true
                },
                socialDescription: {
                    sameAsSEO: true
                }
            };
        }
        this.getCategories();
        this.getWebsitePages();

        if (!this.cmsWebsite.settings) {
            this.$set(this.cmsWebsite, `settings`, {});
        }
        if (!Array.isArray(this.cmsWebsite.settings.languages)) {
            this.$set(this.cmsWebsite.settings, `languages`, []);
        }
        this.syncLanguageSettings();
    },
    methods: {
        normalizeLanguageCodes(languages) {
            if (!Array.isArray(languages)) return [];

            const normalized = [];
            const seen = new Set();

            for (const lang of languages) {
                const code = typeof lang === `string` ? lang : lang?.locale;
                const normalizedCode = normalizeLocaleCode(code);
                if (!normalizedCode) continue;
                const key = normalizedCode.toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                normalized.push(normalizedCode);
            }

            return normalized;
        },
        setPreviewLanguage(language) {
            if (!language) return;
            setLocale(language);
            this.$store.state.previewLanguage = language;
        },
        ensurePreviewLanguage(languages) {
            const normalized = this.normalizeLanguageCodes(languages);
            const current = normalizeLocaleCode(getLocale());
            if (current && normalized.includes(current)) {
                return;
            }
            const fallback = this.fallbackLanguage || getFallbackLocale();
            const next = normalized.includes(fallback) ? fallback : (normalized[0] || getFallbackLocale());
            this.setPreviewLanguage(next);
        },
        ensureFallbackLanguage(languages) {
            const normalized = this.normalizeLanguageCodes(languages);
            const currentFallback = normalizeLocaleCode(this.cmsWebsite?.settings?.fallbackLanguage) || getFallbackLocale();
            const nextFallback = normalized.includes(currentFallback) ? currentFallback : (normalized[0] || getFallbackLocale());
            this.$set(this.cmsWebsite.settings, `fallbackLanguage`, nextFallback);
            setFallbackLocale(nextFallback);
        },
        syncLanguageSettings() {
            const normalized = this.normalizeLanguageCodes(this.cmsWebsite?.settings?.languages);
            const fallback = getFallbackLocale();
            const nextLanguages = normalized.length ? normalized : [fallback];
            this.$set(this.cmsWebsite.settings, `languages`, nextLanguages);
            this.ensureFallbackLanguage(nextLanguages);
            this.ensurePreviewLanguage(nextLanguages);
            if (!this.languageToClear) {
                this.languageToClear = nextLanguages[0] || fallback;
            }
        },
        validateDomain(domain) {
            return RegExp(`^[\\w\\.\\-]*$`).test(domain);
        },
        previewLanguageSelector() {
            this.$store.state.languageSelectModal = true;
        },
        /*
        SCRIPTS
         */
        newScript() {
            this.scriptSelected = {
                name: ``,
                code: ``,
                appendTo: `head`
            };
            this.scriptMode = `add`;
            this.scriptModalVisible = true;
        },
        addScript() {
            this.cmsWebsite.settings.scripts.push(this.scriptSelected);
            this.scriptModalVisible = false;
        },
        editScript(script) {
            this.scriptSelected = script;
            this.scriptMode = `edit`;
            this.scriptModalVisible = true;
        },
        saveScript() {
            this.scriptModalVisible = false;
        },
        removeScript(index) {
            this.cmsWebsite.settings.scripts.splice(index, 1);
        },
        /*
        REDIRECTS
         */
        newRedirect() {
            this.redirectSelected = {
                from: ``,
                to: ``,
                comment: ``,
                exactMatch: false,
                dateCreation: new Date()
            };
            this.redirectMode = `add`;
            this.redirectModalVisible = true;
        },
        addRedirect() {
            this.cmsWebsite.routing.redirects.push(this.redirectSelected);
            this.redirectModalVisible = false;
        },
        editRedirect(redirect) {
            this.redirectSelected = redirect;
            this.redirectMode = `edit`;
            this.redirectModalVisible = true;
        },
        saveRedirect() {
            this.redirectModalVisible = false;
        },
        removeRedirect(index) {
            this.cmsWebsite.routing.redirects.splice(index, 1);
        },
        /*
        ALIASES
         */
        newAlias() {
            this.cmsWebsite.aliases.push({
                domain: ``,
                env: `DEV`
            });
        },
        removeAlias(index) {
            this.cmsWebsite.aliases.splice(index, 1);
        },
        save() {
            this.showLoading();

            this.cmsWebsite.settings.init = true;

            NetworkV2.post(`/api/websites/edit`, this.cmsWebsite)
                .then(resp => {
                    this.showSavedMessage();
                    this.$refs.iframePage.saved(resp.data);
                });
        },
        syncSocial() {
            if (this.cmsWebsite.meta.socialTitle.sameAsSEO) {
                for (let lang in this.cmsWebsite.meta.title) {
                    this.cmsWebsite.meta.socialTitle[lang] = this.cmsWebsite.meta.title[lang];
                }
            }
            if (this.cmsWebsite.meta.socialDescription.sameAsSEO) {
                for (let lang in this.cmsWebsite.meta.description) {
                    this.cmsWebsite.meta.socialDescription[lang] = this.cmsWebsite.meta.description[lang];
                }
            }
        },
        getAllMaps() {
            try {
                NetworkV2.get(`/api/pages/${this.cmsWebsite.dealerid}/list/maps`)
                    .then(resp => {
                        this.availableMaps = resp.data;
                    });
            }
            catch (e) {
                console.error(e);
                swal(`Network Error`, `Couldn't load maps`, `error`);
            }
        },
        getCategories() {
            NetworkV2.get(`/api/cms/categories/${this.dealerid}`)
                .then(resp => {
                    this.categories = resp.data;
                })
        },
        getWebsitePages() {
            NetworkV2.get(`/api/pages/${this.cmsWebsite.dealerid}/list`)
                .then(resp => {
                    this.websitePages.pages = resp.data;
                    this.getAllMaps();
                    this.websitePages.options = [
                        ...new Set(this.websitePages.pages.filter(page => String(page.category || ``).trim() !== ``).map(page => page.category))
                    ].map(cat => {
                        const safeCat = String(cat || ``);
                        const category = safeCat.charAt(0).toUpperCase() + safeCat.slice(1).replace(/-/g, ` `);
                        return {
                            text: category,
                            value: cat
                        }
                    });

                    this.websitePages.options.unshift({
                        text: `All Pages`,
                        value: `all`
                    });
                });
        },
        onCmsPageToggle(page, checked) {
            const idx = this.cmsWebsite.pages.findIndex(p => p.id === page._id);
            if (checked && idx === -1) {
                this.cmsWebsite.pages.push({ id: page._id });
            } else if (!checked && idx !== -1) {
                this.cmsWebsite.pages.splice(idx, 1);
            }

        },
        editPage(page) {
            this.$refs.iframePage.redirect(page);
        },
        openPage(page) {
            window.open(`/cms/preview/website/${this.cmsWebsite.id}/${page._id}`, `_blank`);
        },
        categoryName(category) {
            if (!category) {
                return `No Category|Pas de catégorie`;
            }

            if (!this.categories)
                return category;

            const existing = this.categories.find(c => c.key === category)
            return existing ? existing.name : category;
        }
    }
}
</script>

<style scoped>
.flag-icon {
    width: 20px;
    height: 14px;
    object-fit: contain;
}
</style>
